import { useState, useCallback, useEffect, useRef } from "react";
import utils, { AdUtils } from "../utils";
import { useNavigation } from "../utils/navigation";
import { showSafariAddHomeScreen } from "../stores/GlobalState";
import trackingService from "@/services/trackingService";
import { TrackingEvent, TrackingEventType } from "@/types/tracking.types";
import type {
  DownloadOptions,
  UseDownloadReturn,
  PwaEventType,
} from "../types/useDownload.types";
import { usePlatformLogo } from "../modules/homeDisplay";

export function useDownloadContext(): UseDownloadReturn {
  const env = utils.getEnvInfo();
  const { navigate } = useNavigation();
  const [showProgress, setShowProgress] = useState(false);
  const [isPwaInstalling, setIsPwaInstalling] = useState(false);
  const platformLogo = usePlatformLogo();

  // 监听 PWA 安装接受和拒绝事件
  useEffect(() => {
    const handlePwaAccepted = () => {
      setIsPwaInstalling(true);
      setShowProgress(true);
    };

    const handlePwaRejected = () => {
      setIsPwaInstalling(false);
      setShowProgress(false);
    };

    window.addEventListener("PWA_INSTALL_ACCEPTED", handlePwaAccepted);
    window.addEventListener("PWA_INSTALL_REJECTED", handlePwaRejected);

    return () => {
      window.removeEventListener("PWA_INSTALL_ACCEPTED", handlePwaAccepted);
      window.removeEventListener("PWA_INSTALL_REJECTED", handlePwaRejected);
    };
  }, []);

  const webToPwa = useCallback(async () => {
    return await utils.request("/api/ads/web2pwa", {}, { alert: false });
  }, []);

  const webToApp = useCallback(async () => {
    return await utils.request("/api/ads/web2app", {}, { alert: false });
  }, []);

  const getH5RefAppUrl = useCallback(async (): Promise<string | undefined> => {
    const resp = await utils.request(
      "/api/home/getH5RefAppUrl",
      {},
      { alert: false }
    );
    if (resp.status === 0 && resp?.extra?.[0]) {
      return `https://${resp.extra[0]}`;
    }
  }, []);
  const buildPwaLink = useCallback((): string => {
    let pwaLink = `${window.location.origin}/?pwa=true`;
    if (window.__ch) {
      pwaLink += `&ch=${window.__ch}`;
    }
    if (window.__c) {
      pwaLink += `&c=${window.__c}`;
    }
    return pwaLink;
  }, []);

  const handleIOSInAppWebView = useCallback(() => {
    try {
      const redirected = utils.openExternalBrowserIOS(window.location.href);
      if (!redirected) {
        window.location.href = buildPwaLink();
      }
    } catch {
      window.location.href = buildPwaLink();
    }
  }, []);

  const handleIOSSafariPwa = useCallback(
    async (skipWebToPwa?: boolean) => {
      // 埋点：iOS Safari PWA引导
      trackingService
        .track(TrackingEvent.LANDING_PAGE_CLICK, TrackingEventType.EVENT, {
          eventDetail: "click ios pwa guide",
        })
        .catch(console.error);

      if (!skipWebToPwa) {
        await webToPwa();
      }
      showSafariAddHomeScreen(true);
    },
    [webToPwa]
  );

  const handleIOSSafariDownload = useCallback(
    async (iosLink?: string, skipWebToPwa?: boolean) => {
      // 埋点：iOS下载跳转
      trackingService
        .track(TrackingEvent.LANDING_PAGE_CLICK, TrackingEventType.EVENT, {
          eventDetail: "click ios download",
        })
        .catch(console.error);

      if (!skipWebToPwa) {
        await webToPwa();
      }
      if (iosLink) {
        navigate(iosLink);
        return;
      }

      const h5RefAppUrl = await getH5RefAppUrl();
      if (h5RefAppUrl) {
        navigate(h5RefAppUrl);
      } else {
        showSafariAddHomeScreen(true);
      }
    },
    [webToPwa, navigate, getH5RefAppUrl]
  );

  const handleIOSDownload = useCallback(
    async (options: DownloadOptions) => {
      if (env.context === "InAppWebView") {
        handleIOSInAppWebView();
      } else if (utils.isSafari()) {
        if (options.enablePwa) {
          await handleIOSSafariPwa(options.skipWebToPwa);
        } else {
          await handleIOSSafariDownload(options.iosLink, options.skipWebToPwa);
        }
      } else {
        await handleIOSSafariDownload(options.iosLink, options.skipWebToPwa);
      }
    },
    [
      env.context,
      handleIOSInAppWebView,
      handleIOSSafariPwa,
      handleIOSSafariDownload,
    ]
  );

  const downloadApk = useCallback(
    async (downloadLink?: string) => {
      const envInfo = utils.getEnvInfo();
      const isKnownInApp =
        envInfo.os !== "iOS" && envInfo.browser.endsWith("InApp");
      const isPwa = utils.updateCheckIsPwa();
      // 埋点：点击下载APK

      if (envInfo.context === "InAppWebView" && envInfo.os !== "iOS") {
        utils.tryOpenInExternalBrowser();
        return;
      }
      trackingService
        .track(TrackingEvent.LANDING_PAGE_CLICK, TrackingEventType.EVENT, {
          eventDetail: "click download apk",
        })
        .catch(console.error);

      if (!platformLogo) {
        localStorage.setItem("skip_pwa_guide_page", "true");

        // 获取邀请码
        const invitedCode = utils.getObject("invitedCode");

        if (invitedCode) {
          window.history.replaceState(null, "", `/?p=${invitedCode}`);
        } else {
          window.history.replaceState(null, "", "/");
        }

        // 重新加载页面返回初始画面
        window.location.reload();
      }

      // GP 下载
      if (platformLogo?.appDownloadMethod === "GP" && platformLogo.gpLink) {
        if (isKnownInApp && !isPwa) {
          utils.openDownloadInBrowser(platformLogo.gpLink);
        } else {
          window.open(platformLogo.gpLink, "_blank");
        }
        return;
      }

      // APK 下载
      if (platformLogo?.appDownloadMethod === "APK" && platformLogo.apkFile) {
        const src = utils.getApkDownloadUrl(platformLogo.apkFile);
        //landingDownloadApk();
        webToApp();
        if (isKnownInApp) {
          utils.openDownloadInBrowser(src);
          return;
        }
        utils.downloadLink(src);
        return;
      }

      const link = downloadLink || utils.getApkDownloadLink();
      // 上报下载追踪数据
      //landingDownloadApk();
      webToApp();
      if (link) {
        utils.downloadLink(link);
      }
    },
    [webToApp]
  );

  const handlePwaInstall = useCallback(
    async (skipWebToPwa?: boolean) => {
      if (window.deferredPromptPrompt && window.deferredPrompt) {
        try {
          // 埋点：点击安装PWA
          trackingService
            .track(TrackingEvent.LANDING_PAGE_CLICK, TrackingEventType.EVENT, {
              eventDetail: "click download pwa",
            })
            .catch(console.error);

          if (!skipWebToPwa) {
            await webToPwa();
          }
          window.deferredPromptPrompt();
        } catch (error) {
          console.error("PWA installation failed:", error);
          setIsPwaInstalling(false);
          setShowProgress(false);
          utils.openPwaApp();
        }
      } else {
        // 埋点：点击打开PWA
        trackingService
          .track(TrackingEvent.LANDING_PAGE_CLICK, TrackingEventType.EVENT, {
            eventDetail: "click open pwa",
          })
          .catch(console.error);
        const fpId = utils.getObject("BrowserFingerprintId");
        const sentKey = "a2hsFirstOpen_sent";
        const DAY_30_MS = 30 * 24 * 60 * 60 * 1000;
        const sent = utils.getObject(sentKey) as
          | { fpId: string; time: number }
          | null
          | undefined;
        const isValidSent =
          sent &&
          typeof sent === "object" &&
          sent.fpId &&
          typeof sent.time === "number";
        const shouldSend =
          fpId &&
          (!isValidSent ||
            sent.fpId !== fpId ||
            Date.now() - sent.time > DAY_30_MS);
        if (shouldSend) {
          AdUtils.onCustomEvent("a2hsFirstOpen");
          utils.setObject(sentKey, { fpId, time: Date.now() });
        }
        utils.openPwaApp();
      }
    },
    [webToPwa]
  );

  const handleAndroidDownload = useCallback(
    async (options: DownloadOptions) => {
      if (options.enablePwa && utils.supportPwa()) {
        await handlePwaInstall(options.skipWebToPwa);
      } else {
        await downloadApk(options.apkLink);
      }
    },
    [handlePwaInstall, downloadApk]
  );

  const onDownload = useCallback(
    async (options: DownloadOptions) => {
      if (env.os === "iOS") {
        await handleIOSDownload(options);
      } else {
        await handleAndroidDownload(options);
      }
    },
    [env.os, handleIOSDownload, handleAndroidDownload]
  );

  // PWA 按钮点击处理
  // onDownloadApk: 由组件传入自己的 downloadAndroid 方法，用于降级下载 APK
  const handlePwaButtonClick = useCallback(
    async (apkLink?: string, onDownloadApk?: () => void) => {
      const envInfo = utils.getEnvInfo();
      const fallbackDownload = onDownloadApk || (() => downloadApk(apkLink));

      if (envInfo.os !== "iOS") {
        // 1. in-app WebView（WhatsApp/Instagram 等）：跳出到默认浏览器
        if (envInfo.context === "InAppWebView") {
          utils.tryOpenInExternalBrowser();
          return;
        }

        // 2. 已安装过 PWA：直接打开 PWA 程序
        if (utils.checkPwaEverInstalled()) {
          utils.openPwaApp();
          return;
        }

        // 3. 非 Chrome 浏览器：尝试 intent 跳 Chrome
        //    - 有 Chrome + 选是 → 跳走，Promise 不 resolve，loading 保持直到页面离开
        //    - 有 Chrome + 选否 → resolve("cancelled")，执行降级下载
        //    - 没有 Chrome → resolve("notInstalled")，尝试 PWA 安装或降级
        if (!envInfo.isChrome) {
          const chromeResult = await new Promise<
            "cancelled" | "notInstalled" | "notNeeded"
          >(resolve => {
            const jumped = utils.redirectToChrome(
              () => resolve("cancelled"),
              () => resolve("notInstalled")
            );
            if (!jumped) {
              resolve("notNeeded");
            }
          });

          if (chromeResult === "cancelled") {
            fallbackDownload();
            return;
          }

          if (chromeResult === "notInstalled") {
            if (
              window.deferredPrompt &&
              typeof window.deferredPromptPrompt === "function"
            ) {
              await handlePwaInstall();
            } else {
              fallbackDownload();
            }
            return;
          }
        }

        // 4. 等待 deferredPrompt 就绪（页面刚加载时可能还未触发 beforeinstallprompt）
        if (
          !window.deferredPrompt ||
          typeof window.deferredPromptPrompt !== "function"
        ) {
          await new Promise<void>(resolve => {
            const cleanup = () => {
              window.removeEventListener("beforeinstallprompt", onReady);
              clearTimeout(timeout);
              clearInterval(poll);
            };
            const done = () => {
              cleanup();
              resolve();
            };
            const onReady = () => done();
            window.addEventListener("beforeinstallprompt", onReady);
            const poll = setInterval(() => {
              if (
                window.deferredPrompt &&
                typeof window.deferredPromptPrompt === "function"
              ) {
                done();
              }
            }, 100);
            const timeout = setTimeout(done, 3000);
          });
        }

        // 5. 满足 PWA 条件则安装，否则降级
        if (
          window.deferredPrompt &&
          typeof window.deferredPromptPrompt === "function"
        ) {
          await handlePwaInstall();
        } else {
          fallbackDownload();
        }
      }
    },
    [handlePwaInstall, downloadApk]
  );

  return {
    onDownload,
    progressState: { show: showProgress, setShow: setShowProgress },
    handlePwaButtonClick,
    pwaInstallingState: {
      isInstalling: isPwaInstalling,
      setIsInstalling: setIsPwaInstalling,
    },
  };
}

export function usePwaEvent(): PwaEventType {
  const [event, setEvent] = useState<PwaEventType>();
  const setEventRef = useRef<(eventType: PwaEventType) => void>();

  setEventRef.current = (eventType: PwaEventType) => {
    setEvent(eventType);
  };

  useEffect(() => {
    const handleAppInstalled = () => {
      setEventRef.current?.("appinstalled");
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  return event;
}
